import type * as IAP from 'SwagExtensionStore/module/sw-in-app-purchases/types';
import template from './sw-in-app-purchase-checkout.html.twig';
import './sw-in-app-purchase-checkout.scss';
import { trackExtensionStoreEvent } from 'SwagExtensionStore/util/telemetry';
import { hasDemoShopBundle } from 'SwagExtensionStore/util/demo-shop';

/**
 * @private
 */
export default Shopware.Component.wrapComponentConfig({
    template,

    inject: [
        'inAppPurchasesService',
    ],

    mixins: [
        Shopware.Mixin.getByName('notification'),
    ],

    data() {
        return {
            state: 'loading' as 'loading' | 'purchase' | 'error' | 'success' | 'info',
            store: Shopware.Store.get('inAppPurchaseCheckout'),
            inAppPurchaseCart: null as IAP.InAppPurchaseCart | null,
            extension: null as IAP.Extension | null,
            purchase: null as IAP.InAppPurchase | null,
            tosAccepted: false,
            gtcAccepted: false,
            variant: null as string | null,
            reason: null as string | null,
        };
    },

    created() {
        this.createdComponent();
    },

    computed: {
        assetFilter() {
            return Shopware.Filter.getByName('asset');
        },
        extensionIcon() {
            if (this.extension?.icon) {
                return this.extension.icon;
            }

            if (this.extension?.iconRaw) {
                return `data:image/png;base64, ${this.extension.iconRaw}`;
            }

            return this.assetFilter('/swagextensionstore/administration/static/img/theme/default_theme_preview.jpg');
        },
    },

    methods: {
        createdComponent() {
            this.store.$subscribe(() => {
                this.requestFeature();
            });
        },

        createCart(variant: string) {
            if (!this.store.extension || !this.store.entry || !variant) {
                this.reset();
                return;
            }

            this.variant = variant;
            this.state = 'loading';

            this.inAppPurchasesService.createCart(
                this.store.extension,
                this.store.entry.identifier,
                variant,
            ).then((cart) => {
                this.inAppPurchaseCart = cart;
                this.state = 'purchase';
                this.trackCheckoutEvent('initiated');
            }).catch ((errorResponse: ErrorResponse) => {
                Shopware.Utils.debug.error('checkout-iap', errorResponse);
                this.reason = this.getErrorApiCode(errorResponse);
                this.state = 'error';
            });
        },

        async requestFeature() {
            if (!this.store.extension || !this.store.entry) {
                this.reset();
                return;
            }

            // Demo shops can never complete an in-app purchase, so the request is skipped entirely
            // instead of letting the API reject it. Must not reset, that would close the modal.
            if (hasDemoShopBundle()) {
                this.reason = 'demoShop';
                this.state = 'info';
                return;
            }

            this.state = 'loading';

            await Promise.all([
                this.inAppPurchasesService.getExtension(this.store.extension),
                this.inAppPurchasesService.getPriceModels(this.store.extension, this.store.entry.identifier),
            ]).then(([extension, purchase]) => {
                this.extension = extension;
                this.purchase = purchase;

                if (!this.purchase) {
                    throw new Error('No in-app purchase found');
                }

                return this.createCart(this.purchase.preselectedVariant);
            }).catch ((errorResponse: ErrorResponse)=> {
                Shopware.Utils.debug.error('checkout-iap', errorResponse);
                this.reason = this.getErrorApiCode(errorResponse);
                this.state = 'error';
            });
        },

        onPurchaseFeature() {
            if (!this.inAppPurchaseCart || !this.extension) {
                this.reset();
                return;
            }

            this.trackCheckoutEvent('confirmed');

            this.inAppPurchasesService.orderCart(
                this.inAppPurchaseCart.taxRate,
                this.inAppPurchaseCart.positions,
                this.extension.name,
            ).then(() => {
                this.state = 'success';
                this.trackCheckoutEvent('successful');
            }).catch((errorResponse: ErrorResponse) => {
                Shopware.Utils.debug.error('checkout-iap', errorResponse);
                this.reason = this.getErrorApiCode(errorResponse);
                this.state = 'error';
                this.trackCheckoutEvent('failed');
            });
        },

        handleStateActions(isButton: boolean) {
            switch (this.state) {
                case 'purchase':
                    if (isButton) {
                        this.onPurchaseFeature();
                    } else {
                        this.reset();
                    }
                    break;
                case 'error':
                    if (isButton) {
                        this.requestFeature();
                    } else {
                        this.reset();
                    }
                    break;
                case 'success':
                    this.reset();
                    this.inAppPurchasesService.refreshInAppPurchases()
                        .then(() => {
                            window.location.reload();
                        });
                    break;
                default:
                    this.reset();
                    break;
            }
        },

        getErrorApiCode(errorResponse: ErrorResponse): string | null {
            return errorResponse?.response?.data.errors[0]?.apiCode ?? null;
        },

        reset() {
            if (this.state !== 'success') {
                this.trackCheckoutEvent('cancelled');
            }

            this.store.dismiss();
            this.inAppPurchaseCart = null;
            this.extension = null;
            this.reason = null;
            this.state = 'loading';
            this.purchase = null;
            this.variant = null;
            this.tosAccepted = false;
            this.gtcAccepted = false;
        },

        trackCheckoutEvent(name: string) {
            const [position] = this.inAppPurchaseCart?.positions ?? [];
            if (!position || !this.extension) {
                return;
            }

            trackExtensionStoreEvent(`iap_checkout_${name}`, {
                extension_id: this.extension.id,
                extension_name: this.extension.name,
                feature_identifier: position.feature.identifier,
                feature_variant: position.variant,
                net_price: position.netPrice,
                tax_rate: position.taxRate,
                pseudo_price: position.feature.price,
                prorated_net_price: position.proratedNetPrice,
                next_booking_date: position.nextBookingDate,
                subscription_change: position.subscriptionChange?.type ?? null,
            });
        },
    },
});
